import React, { Component } from 'react'
import { connect } from 'react-redux'
import { FormattedHTMLMessage, injectIntl } from 'react-intl'
import { Helmet } from 'react-helmet-async'
import { Input, InputGroup, InputGroupAddon } from 'reactstrap'
import { withRouter } from 'react-router'

import FormattedMessage from '../../components/FormattedMessage/FormattedMessage'
import faqActions from '../../redux/actions/faq.actions'
import { FAQ_PENDING_PATH } from '../../services/faq.service'
import Loader from '../../components/Loader/Loader'
import stripHTMLFormatter from '../../utilities/stripHTMLStringFormatter'

import FaqCategory from '../../components/Faq/FaqElasticSearch/FaqCategory'
import FaqTagCloud from '../../components/Faq/FaqElasticSearch/FaqTagCloud'
import FaqResultModal from '../../components/Faq/FaqElasticSearch/FaqResultModal'

import { currentUser } from '../../redux/reducers/user.reducers'

import './Faq.scss'

const FAQ_USER_PATH = 'amd_faq_user_path'
const REFINED_SEARCH_TYPE = {
  searchBar: 'search',
  tagCloud: 'tagCloud',
}

class FaqES extends Component {
  constructor(props) {
    super(props)
    this.state = {
      searchString: '',
      modalOpen: false,
      lastFaqCategoryId: null,
      modalType: null,
    }
    this._isMounted = false
  }

  async componentDidMount() {
    this._isMounted = true
    const { getFaqs, getFaqsCategories, getFaqTagCloud, history } = this.props
    await getFaqs()
    await getFaqsCategories()
    await getFaqTagCloud()

    const pendingPath = sessionStorage.getItem(FAQ_PENDING_PATH)
    if (pendingPath) {
      sessionStorage.removeItem(FAQ_PENDING_PATH)
      history.push(pendingPath)
      return
    }

    if (this._isMounted) {
      this.getlastFaqCategory()
    }
  }

  componentWillUnmount() {
    this._isMounted = false
  }

  handleOnChange = e => {
    this.setState({
      searchString: e.target.value,
    })
  }

  handleRefinedSearch(searchTerm, type) {
    const { getRefinedFaqs } = this.props
    getRefinedFaqs(searchTerm, type).then(() => {
      if (this._isMounted) {
        this.toggleModal()
      }
    })
  }

  // get last faq item user navigated to
  getlastFaqCategory() {
    if (!this._isMounted) return
    const storedFaqCategoryId = window.localStorage.getItem(FAQ_USER_PATH)
    const lastFaqCategory = storedFaqCategoryId ? JSON.parse(storedFaqCategoryId) : null
    const lastFaqCategoryId = lastFaqCategory && lastFaqCategory.id

    if (lastFaqCategory) {
      this.setState({
        lastFaqCategoryId,
      })
    }
  }

  handleSearch = async e => {
    if (e.key === 'Enter') {
      const { searchString } = this.state
      const sanitizedSearchString = stripHTMLFormatter(searchString)
      this.handleRefinedSearch(sanitizedSearchString, REFINED_SEARCH_TYPE.searchBar)
      this.setState(() => ({
        modalType: REFINED_SEARCH_TYPE.searchBar,
      }))
    }
  }

  handleTag = tag => {
    this.handleRefinedSearch(tag, REFINED_SEARCH_TYPE.tagCloud)
    this.setState(() => ({
      modalType: REFINED_SEARCH_TYPE.tagCloud,
    }))
  }

  handleSearchButtonClick = () => {
    const { searchString } = this.state
    if (searchString.length === 0) return

    const sanitizedSearchString = stripHTMLFormatter(searchString)
    this.handleRefinedSearch(sanitizedSearchString, REFINED_SEARCH_TYPE.searchBar)
    this.setState(() => ({
      modalType: REFINED_SEARCH_TYPE.searchBar,
    }))
  }

  handleFaqLog = async (faqId, source) => {
    const { saveFaqLog, user } = this.props
    const payload = {
      faqId,
      userId: user.id,
      source,
    }
    await saveFaqLog(payload)
  }

  toggleModal = () => {
    this.setState(state => ({
      modalOpen: !state.modalOpen,
    }))
  }

  render() {
    const { faqs, faqCategories, faqLoading, tagCloud, refinedFaqs = [], intl } = this.props
    const { modalOpen, modalType, lastFaqCategoryId } = this.state
    const pageTitleLabel = intl.formatMessage({ id: 'global.pageTitles.faq' })
    const placeholderLabel = intl.formatMessage({ id: 'global.label.search' })

    return (
      <div className="container mt-5 mb-5" aria-labelledby="faq-elastic-page-title">
        <Helmet>
          <title>{pageTitleLabel}</title>
        </Helmet>
        <h1 className="page-title" id="faq-elastic-page-title">
          <FormattedMessage id="page.faqPage.title.heroTitle" />
        </h1>
        <h4>
          <FormattedMessage id="page.faqPage.title.description" />
        </h4>
        <p>
          <FormattedHTMLMessage id="page.faqPage.title.note" />
        </p>

        {faqLoading ? (
          <Loader />
        ) : (
          <>
            <FaqResultModal
              modalType={modalType}
              isOpen={modalOpen}
              toggle={this.toggleModal}
              faqs={refinedFaqs}
              handleFaqLog={this.handleFaqLog}
            />
            <div className="faq">
              <InputGroup>
                <Input
                  id="faq-search-input"
                  name="faq-search"
                  placeholder={`${placeholderLabel}...`}
                  onKeyDown={this.handleSearch}
                  onChange={this.handleOnChange}
                  aria-label={placeholderLabel}
                />
                <InputGroupAddon addonType="append">
                  <button
                    type="button"
                    className="faq__searchIcon"
                    onClick={this.handleSearchButtonClick}
                    aria-label={placeholderLabel}
                  >
                    <i className="fas fa-search" aria-hidden="true" />
                  </button>
                </InputGroupAddon>
              </InputGroup>
              <FaqTagCloud tagCloud={tagCloud} handleTag={this.handleTag} />
              <FaqCategory
                categories={faqCategories}
                faqs={faqs}
                lastFaqCategoryId={lastFaqCategoryId || null}
                handleFaqLog={this.handleFaqLog}
              />
            </div>
          </>
        )}
      </div>
    )
  }
}

const mapDispatchToProps = dispatch => ({
  getFaqs: () => dispatch(faqActions.getFaqs()),
  getRefinedFaqs: (...args) => dispatch(faqActions.getRefinedFaqs(...args)),
  getFaqsCategories: () => dispatch(faqActions.getFaqsCategories()),
  getFaqTagCloud: () => dispatch(faqActions.getFaqTagCloud()),
  setActiveFaq: (...args) => dispatch(faqActions.setActiveFaq(...args)),
  saveFaqLog: (...args) => dispatch(faqActions.saveFaqLog(...args)),
})

const mapStateToProps = state => ({
  user: currentUser(state),
  faqLoading: state.faqReducer.faqLoading,
  faqs: state.faqReducer.faq,
  refinedFaqs: state.faqReducer.refinedFaqs,
  faqCategories: state.faqReducer.faqCategories,
  tagCloud: state.faqReducer.tagCloud,
})

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withRouter(FaqES)))
